import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/authService';
import PageHeader from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(t('pages.profile.errorAllFieldsRequired'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('pages.profile.errorPasswordMismatch'));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('pages.profile.errorMinLength'));
      return;
    }

    try {
      setLoading(true);
      const result = await authService.changePassword(currentPassword, newPassword);

      if (result.success) {
        setSuccess(result.message || t('pages.profile.successMessage'));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(result.message || t('pages.profile.errorUpdateFailed'));
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      const errorMessage = error.response?.data?.detail || error.message || t('pages.profile.errorUpdateFailed');
      setError(errorMessage);
      console.error('Password change error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-40 text-slate-500 text-sm">
        {t('common.loading')}
      </div>
    );
  }

  const initials = `${user.first_name?.trim()[0] ?? ''}${user.family_name?.trim()[0] ?? ''}`.toUpperCase();

  return (
    <div className="min-h-full bg-surface-muted">
      <PageHeader
        title={t('pages.profile.title')}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>
              {t('navigation.home')}
            </Button>
            <Button variant="destructive" size="sm" onClick={logout}>
              {t('navigation.logout')}
            </Button>
          </div>
        }
      />

      <div className="p-6 max-w-lg">
        {/* Avatar + info */}
        <div className="bg-white rounded-lg p-5 mb-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-600 text-white font-semibold text-base flex-shrink-0">
              {initials}
            </div>
            <div>
              <p className="font-semibold text-slate-900">{user.first_name} {user.family_name}</p>
              <p className="text-sm text-slate-500">{user.role}</p>
            </div>
          </div>
          <Separator className="mb-4" />
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-slate-500">{t('pages.profile.emailLabel')}</dt>
            <dd className="text-slate-900">{user.email}</dd>
            <dt className="text-slate-500">{t('pages.profile.usernameLabel')}</dt>
            <dd className="text-slate-900 font-mono text-xs">{user.username}</dd>
            <dt className="text-slate-500">{t('pages.profile.roleLabel')}</dt>
            <dd className="text-slate-900">{user.role}</dd>
          </dl>
        </div>

        {/* Change password */}
        <div className="bg-white rounded-lg p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">{t('pages.profile.changePassword')}</h2>

          {error && (
            <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 px-3 py-2.5 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Hidden username for password managers */}
            <input type="text" name="username" autoComplete="username"
              value={user.email || user.username} readOnly className="sr-only" />

            <div className="space-y-1.5">
              <Label htmlFor="currentPassword">{t('pages.profile.currentPassword')}</Label>
              <Input type="password" id="currentPassword" name="current-password"
                placeholder={t('pages.profile.currentPasswordPlaceholder')}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                onInput={() => { setError(''); setSuccess(''); }}
                required autoComplete="current-password" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="newPassword">{t('pages.profile.newPassword')}</Label>
              <Input type="password" id="newPassword" name="new-password"
                placeholder={t('pages.profile.newPasswordPlaceholder')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onInput={() => { setError(''); setSuccess(''); }}
                required autoComplete="new-password" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword">{t('pages.profile.confirmPassword')}</Label>
              <Input type="password" id="confirmPassword" name="confirm-password"
                placeholder={t('pages.profile.confirmPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onInput={() => { setError(''); setSuccess(''); }}
                required autoComplete="new-password" />
            </div>

            <Button type="submit" disabled={loading} className="mt-1">
              {loading ? t('pages.profile.updating') : t('pages.profile.updateButton')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
